/**
 * The year-by-year net worth line of a projection, reduced to the few fields worth PERSISTING.
 *
 * A saved strategy has always carried `legacyEstimate` — the net worth of the FINAL projection row —
 * and nothing else from the walk. That answers "what is left to my estate?" and no other question
 * about time. "What will I be worth at the start of FY27/28?" is a question about a row in the middle,
 * and the row was thrown away the moment the strategy was saved.
 *
 * Re-running the engine to recover it is not an option for the surfaces that need it (see the header
 * of server/src/rest/ai/affordabilitySummary.ts): the run's inputs are live UI state, and a re-run
 * would answer a DIFFERENT question from the one on the user's screen while sounding just as certain.
 * So the walk's net worth line is saved with the strategy, at the moment it is struck, from the same
 * `projections` array `legacyEstimate` is already read out of.
 *
 * WHAT IS AND IS NOT KEPT. Only the balance-sheet date and the figure struck on it. Not the breakdown
 * rows, not the cashflow, not the HTML — a projection row is large, this is saved inside an encrypted
 * fact-find, and the whole schedule has to stay small enough that persisting it is uncontroversial.
 * Formatting is likewise NOT done here: numbers stay numbers and dates stay ISO, so a consumer that
 * needs them rendered (the AI affordability payload formats every figure as a string) can do so in its
 * own vocabulary, and one that needs to compute is not handed prose.
 */

/** The projection-row fields this reads. Structural, so both the engine's rows and the frontend's fit. */
export interface ProjectionNetWorthRow {
    netWorth?: unknown;
    fyLabel?: unknown;
    periodEnd?: unknown;
    projectionPeriodMode?: unknown;
    isPartialFy?: unknown;
    year?: unknown;
}

/** One balance-sheet date in the projection, and what the client is worth on it. */
export interface NetWorthSchedulePoint {
    /** The row's own label — `FY26/27`, or `2027` on a calendar-year run. */
    period: string;
    /** ISO instant the row CLOSES on, which is the date this net worth is struck. Null in strategy-year mode, which dates nothing. */
    asAt: string | null;
    /** The reporting calendar the run was cut on — carried per point so a stored schedule stays self-describing. */
    mode: string;
    /**
     * True on a part-year row (the first, when the projection is anchored mid-period).
     *
     * It does NOT scale the figure. Net worth is a stock read at `asAt`, not a flow accrued across the
     * period, so a part-year row's net worth is as complete as any other's — unlike its cashflow, which
     * is why the engine reports partial-period variants of those and none for this.
     */
    partial: boolean;
    netWorth: number;
}

/**
 * Cap on stored points, as an anti-bloat bound rather than a modelling one.
 *
 * The horizon the UI offers is 5–10 years and the engine is driven by `legacyYears` from the same
 * screen, so no legitimate run comes close. This exists because the schedule lands in an encrypted
 * fact-find blob that is written, read and re-written on every autosave.
 */
export const MAX_NET_WORTH_SCHEDULE_POINTS = 60;

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/**
 * Reduce a projection run to its net worth line.
 *
 * Rows with no usable net worth are DROPPED rather than zero-filled: a missing figure and a projected
 * net worth of nothing are different facts, and only one of them should ever be quoted back to a client.
 */
export function buildNetWorthSchedule(projections: readonly ProjectionNetWorthRow[] | null | undefined): NetWorthSchedulePoint[] {
    if (!Array.isArray(projections)) return [];

    return projections
        .slice(0, MAX_NET_WORTH_SCHEDULE_POINTS)
        .flatMap((row, index) => {
            if (!isFiniteNumber(row?.netWorth)) return [];
            const label = typeof row.fyLabel === 'string' && row.fyLabel ? row.fyLabel : `Year ${isFiniteNumber(row.year) ? row.year : index + 1}`;
            return [
                {
                    period: label,
                    asAt: typeof row.periodEnd === 'string' && row.periodEnd ? row.periodEnd : null,
                    mode: typeof row.projectionPeriodMode === 'string' && row.projectionPeriodMode ? row.projectionPeriodMode : 'financial_year',
                    partial: row.isPartialFy === true,
                    netWorth: row.netWorth,
                },
            ];
        });
}

/**
 * The calendar year a period CLOSES in, read out of its own label.
 *
 * Deliberately NOT read from `asAt`, even though that field is right there. `asAt` is an ISO instant
 * built from a local-midnight `Date` in whichever timezone ran the projection, so `2027-06-30` in
 * Sydney is stored as `2027-06-29T14:00:00Z`. Formatting or incrementing that from a server on a
 * different offset moves the date by a day — and every date this module deals in is a period boundary,
 * the one place a one-day slip silently becomes a one-YEAR slip. The label carries the same fact with
 * no timezone attached, so it is the safer source. `asAt` remains for consumers that want the instant.
 */
function closingYearOf(point: NetWorthSchedulePoint): number | null {
    if (point.mode === 'calendar_year') {
        return /^\d{4}$/.test(point.period) ? Number(point.period) : null;
    }
    if (point.mode !== 'financial_year') return null;
    const match = /^FY(\d{2})\/\d{2}$/.exec(point.period);
    // The FY opens in `2000 + a` and closes in the year after — which is how FY99/00 stays coherent
    // without a century special case.
    return match ? 2000 + Number(match[1]) + 1 : null;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * The date this period's balance sheet is struck on — "30 June 2027", or "31 December 2027" on a
 * calendar-year run. Null when the label cannot be read (strategy-year mode dates nothing).
 */
export function periodClosingDateLabel(point: NetWorthSchedulePoint): string | null {
    const year = closingYearOf(point);
    if (year === null) return null;
    const [day, monthIndex] = point.mode === 'calendar_year' ? [31, 11] : [30, 5];
    return `${day} ${MONTH_NAMES[monthIndex]} ${year}`;
}

/**
 * The period that BEGINS where this one ends — `FY26/27` → `FY27/28`, `2027` → `2028`.
 *
 * The reason this exists at all: a client asks what they will be worth "at the start of FY27/28", and
 * the projection has no such row. It has a row that CLOSES on 30 June 2027, and that date is both the
 * end of FY26/27 and the start of FY27/28 — the same balance sheet under two names. Naming both is
 * what lets a reader (or a language model) match the question to the row without doing arithmetic on
 * year numbers, which is exactly the step that produces a confident answer one year out.
 */
export function periodLabelFollowing(point: NetWorthSchedulePoint): string | null {
    const closingYear = closingYearOf(point);
    if (closingYear === null) return null;
    if (point.mode === 'calendar_year') return String(closingYear + 1);
    const twoDigit = (year: number) => String(year % 100).padStart(2, '0');
    return `FY${twoDigit(closingYear)}/${twoDigit(closingYear + 1)}`;
}
